import { type MouseEvent, type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";

interface AppFrameProps {
  header: ReactNode;
  bottomBar: ReactNode;
  children: ReactNode;
}

/**
 * The signed-in page's frame: a 4px brand rule, the skip link, the header,
 * the main region and the phone bottom bar. The main region owns the page's
 * gutters and its 1140px cap, and below `lg` it reserves the bottom bar's
 * height plus the safe-area inset so the last row of a page clears the bar.
 */
export function AppFrame({ header, bottomBar, children }: AppFrameProps) {
  const { t } = useTranslation("customer");
  const mainRef = useRef<HTMLElement>(null);

  // The router owns the URL, so the skip link moves focus itself rather than
  // following its fragment.
  const skipToMain = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    mainRef.current?.focus();
  };

  return (
    <div className="relative flex min-h-dvh flex-col bg-canvas">
      <div aria-hidden="true" className="h-1 shrink-0 bg-brand-fg" />
      <a
        href="#main"
        onClick={skipToMain}
        className="absolute top-3 left-4 z-50 -translate-y-[calc(100%+1rem)] rounded-[8px] border border-line-strong bg-surface px-3 py-2 text-sm font-semibold text-ink focus:translate-y-0"
      >
        {t("shell.skipToContent")}
      </a>
      {header}
      <main
        ref={mainRef}
        id="main"
        tabIndex={-1}
        className="flex-1 max-lg:pb-[calc(var(--spacing-bottom-bar)+env(safe-area-inset-bottom))]"
      >
        <div className="mx-auto w-full max-w-[1140px] px-4 pt-6 pb-8 lg:px-9 lg:pt-9 lg:pb-10">
          {children}
        </div>
      </main>
      {bottomBar}
    </div>
  );
}
